<template>
  <div
    class="min-h-screen w-full pt-header-mobile-height tablet:pt-header-height"
  >
    <LazyLayoutHeader />
    <main v-show="!isAppLoading" class="content-height">
      <slot />
    </main>
    <LoaderStack
      v-if="isAppLoading"
      class="content-height flex w-full items-center justify-center bg-white"
    />
    <LazyLayoutFooter />
    <LazyLayoutFloatButtons />
    <!-- TODO: 待 CN 選單確定改版後移除 -->
    <LazyLayoutCNMenuDesktop v-if="isCN" />
    <LazyLayoutMenuDesktop v-else />
    <LazyLayoutPopUpLoginError @close="setRegionLocalePopUp" />
    <LazyLayoutPopUpLoginFailed @close="setRegionLocalePopUp" />
    <LazyLayoutPopUpMaintenance />
    <LazyLayoutPopUpOrganization />
    <LazyLayoutPopUpRegionLocale />
    <LazyLayoutSearch />
    <LazyLayoutCNMenuMobile v-if="isCN" />
    <LazyLayoutMenuMobile v-else />
  </div>
</template>

<script setup lang="ts">
  import Cookies from 'js-cookie';
  import _ from 'lodash';

  import { EnumLoginErrorType, EnumRegionErrorType } from '@/stores/mainStore';

  const route = useRoute();
  const router = useRouter();
  const mainStore = useMainStore();
  const menuStore = useMenuStore();
  const profileStore = useProfileStore();

  const {
    serverIp,
    clientIp,
    isInitialized,
    isAppLoading,
    regionData,
    loginState,
    matchedLocale,
  } = storeToRefs(mainStore);

  const { profile, isUserVerifying } = storeToRefs(profileStore);
  const { isSupportRegionLocale, currentRegion, isCN } = useLanguage();
  const { SESSION_KEY, getClosedPopUpRegions } = useClosedPopUpRegions();

  /**
   * 登入後的區域語系設定
   */

  const regionErrorType = ref<EnumRegionErrorType | null>(null);
  const regionLocalePopUpTimer = ref<NodeJS.Timeout | null>(null);

  const setRegionLocale = async (): Promise<void> => {
    const cookie = useCookie('REGION_LOCALE_ERROR_TYPE', {
      domain: location.hostname,
      secure: true,
      path: '/',
    });

    const { query } = route;

    regionErrorType.value = (cookie.value as EnumRegionErrorType) || null;

    cookie.value = null;
    Cookies.remove('REGION_LOCALE_ERROR_TYPE');

    // 判斷 IP 是否有變更

    if (clientIp.value && serverIp.value && clientIp.value !== serverIp.value) {
      sessionStorage.removeItem(SESSION_KEY);
    }

    if (regionData.value.isLogin) {
      regionErrorType.value = null;

      if (!isSupportRegionLocale.value) {
        // 導向對應的地區語系頁面時顯示滿版 Loading
        isAppLoading.value = true;

        regionErrorType.value = EnumRegionErrorType.D;

        const pathSegments = route.path.split('/');
        pathSegments[1] = matchedLocale.value;
        const path = pathSegments.join('/');
        await router.replace({ path, query });
      }
    }

    if (!regionData.value.isLogin) {
      const closedList = getClosedPopUpRegions();
      const closedListSet: Set<string> = new Set(closedList);
      if (
        regionErrorType.value === EnumRegionErrorType.A &&
        closedListSet.has(currentRegion.value || '')
      ) {
        regionErrorType.value = null;
      }
    }
  };

  const setRegionLocalePopUp = (): void => {
    if (regionErrorType.value) {
      nextTick(() => {
        regionLocalePopUpTimer.value = setTimeout(() => {
          regionData.value.errorType = regionErrorType.value;
        }, 1000);
      });
    }
  };

  const setLoginStatePopUp = (): void => {
    /**
     * 如果 QueryString error = 1（異常處理 E）或是 error = 2（異常處理 F）就顯示 PopUp，並移除其 QueryString
     * 如果 QueryString role = 0 就顯示 PopUp，並移除其 QueryString
     */

    const { error, role } = route.query;

    if (error === '1' || error === '2' || role === '0') {
      const query = _.omit(route.query, ['error', 'role']);
      router.replace({ query });

      if (role === '0') {
        loginState.value.isFailed = true;
      }
    } else {
      setRegionLocalePopUp();
    }

    switch (error) {
      case '1':
        loginState.value.errorType = EnumLoginErrorType.E;
        break;
      case '2':
        loginState.value.errorType = EnumLoginErrorType.F;
        break;
      default:
        loginState.value.errorType = null;
        break;
    }
  };

  /**
   * Fetch Menu
   */

  await menuStore.USE_GET_MENU();
  await menuStore.USE_GET_SOLUTION_MENU();
  await menuStore.USE_GET_PRODUCTS_MENU();

  const { execute: GET_CLIENT_IP } = await mainStore.USE_GET_CLIENT_IP();

  /**
   * * NOTE:
   * * isInitialized: 是否已完成網站初始化
   * * isAppLoading: 是否顯示滿版 Loading，使用者登入後若是區域語系不符，需要導向對應的地區語系頁面時才會顯示
   * * isUserVerifying: 是否正在載入使用者資料，在 serverSide 會被設為 true，clientSide 驗證完 CHECK_LOGIN 後若是未登入會設為 false，判斷為已登入的話會等到取得使用者資料後設置為 false
   */

  onMounted(async () => {
    await mainStore.GET_CHECK_LOGIN();

    if (!regionData.value.isLogin) {
      isUserVerifying.value = false;
    }

    await GET_CLIENT_IP();
    await setRegionLocale();
    await profileStore.GET_PROFILE();
    setLoginStatePopUp();

    nextTick(() => {
      isAppLoading.value = false;
      isInitialized.value = true;
    });
  });

  onBeforeUnmount(() => {
    if (regionLocalePopUpTimer.value) {
      clearTimeout(regionLocalePopUpTimer.value);
    }
  });
</script>

<style lang="scss" scoped>
  .content-height {
    // 內容區塊高度 = 螢幕高度 - (Header 高度 + Footer 高度)

    @apply min-h-[calc(100vh-theme(spacing.header-mobile-height)-theme(spacing.footer-mobile-height))] tablet:min-h-[calc(100vh-theme(spacing.header-height)-theme(spacing.footer-height))];
  }
</style>
